import {
  RefreshCwIcon,
  CircleCheckIcon,
  CircleAlertIcon,
  TrashIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { CellProps, Column } from "react-table";

import {
  ConnectionsDocument,
  ConnectionsWithUsageSummaryDocument,
  ConnectionsWithUsageSummaryQuery,
  IntegrationAbility,
  useRefreshDestinationMutation,
  useRemoveConnectionMutation,
  useRestrictedConnectorsQuery,
} from "@/apollo/types";
import { Badge } from "@/components/elements/Badge";
import ConfirmDeleteModal from "@/components/elements/ConfirmDeleteModal";
import { createGlobalFilterFn } from "@/components/elements/DataTable";
import { createStickyMenuColumn } from "@/components/elements/DataTable/helpers/create-sticky-menu-column";
import IntegrationCellTitle from "@/components/elements/IntegrationCellTitle";
import { List } from "@/components/elements/List";
import MenuItem from "@/components/elements/MenuItem";
import Tooltip from "@/components/elements/Tooltip";
import { TextMuted } from "@/components/elements/Typography";
import TableMenu from "@/components/modules/TableMenu";
import { useFilteredNotifications } from "@/features/notifications";
import { usePermission } from "@/features/roleAccess";
import { dateTimeFormat } from "@/helpers/dateFormat";
import useDeleteItem from "@/hooks/useDeleteItem";
import { useIntegrationsMap } from "@/integrations";
import { useToast } from "@/providers/ToastProvider";
import dayjs from "@/utils/date";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormatTime } from "@/components/elements/FormatTime";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";

export type ConnectionData = ConnectionsWithUsageSummaryQuery["connectors"][0];

export const globalFilterFn = createGlobalFilterFn<ConnectionData>((d) => [
  d.label,
  d.integrationId,
]);

/**
 * Which connectors are restricted, and the Access groups granted on each.
 *
 * `skip` rather than a conditional hook call: this list renders for every role, and
 * `restrictedConnectors` is admin-only, so firing it for a viewer would 403 on load.
 * There is no field on `Connection` saying whether it is restricted — showing a
 * non-admin that much without revealing who may read it needs new backend support.
 */
function useRestrictedConnectorGroups() {
  const { dataAccessControlEnabled } = useFlags();
  const canManageAccessGroups = usePermission("manageAccessGroups");

  const { data } = useRestrictedConnectorsQuery({
    skip: dataAccessControlEnabled !== true || !canManageAccessGroups,
  });

  return useMemo(
    () =>
      new Map(
        (data?.restrictedConnectors ?? []).map((restriction) => [
          restriction.connectionId,
          restriction.accessGroups.map((group) => group.name),
        ]),
      ),
    [data?.restrictedConnectors],
  );
}

export function useConnectionsColumns(): Column<ConnectionData>[] {
  const integrations = useIntegrationsMap();
  const canDeleteConnection = usePermission("deleteConnection");
  const canUpdateConnection = usePermission("updateConnection");
  const restrictedGroups = useRestrictedConnectorGroups();

  return useMemo<Column<ConnectionData>[]>(
    () => [
      {
        Header: "Connector",
        Cell: ({
          value: integrationId,
        }: CellProps<ConnectionData, ConnectionData["integrationId"]>) => {
          return (
            <IntegrationCellTitle
              integrationId={integrationId}
              connectionLabel={integrations.get(integrationId)?.name}
            />
          );
        },
        accessor: "integrationId",
        sortType: (rowA, rowB) => {
          const getSortValue = (row: ConnectionData) => {
            const val = integrations.get(row.integrationId)?.name;
            return val || row.integrationId;
          };
          return getSortValue(rowA.original).localeCompare(
            getSortValue(rowB.original),
          );
        },
      },
      {
        Header: "Name",
        Cell: ({
          row,
          value: label,
        }: CellProps<ConnectionData, ConnectionData["label"]>) => {
          const grantedGroups = restrictedGroups.get(row.original.id);
          return (
            <div className="flex items-center gap-1.5">
              {label ? (
                <span>{label}</span>
              ) : (
                <Tooltip content="Name not set, using ID instead">
                  <span className="text-gray-300">
                    {row.original.integrationId}
                  </span>
                </Tooltip>
              )}
              {grantedGroups && (
                <Tooltip
                  content={
                    grantedGroups.length > 0
                      ? `Readable by members of: ${grantedGroups.join(", ")}`
                      : "Restricted with no Access groups granted, so only admins can read it"
                  }
                >
                  <Badge variant="secondary">Restricted</Badge>
                </Tooltip>
              )}
            </div>
          );
        },
        accessor: "label",
        sortType: (rowA, rowB) => {
          const getSortValue = (row: ConnectionData) => {
            const val = typeof row.label === "string" ? row.label.trim() : "";
            return val.length > 0 ? val : row.integrationId;
          };
          return getSortValue(rowA.original).localeCompare(
            getSortValue(rowB.original),
          );
        },
      },
      {
        Header: "Created",
        Cell: ({
          value: createdAt,
        }: CellProps<ConnectionData, ConnectionData["createdAt"]>) => {
          return (
            <Tooltip content={dayjs(createdAt).format(dateTimeFormat)}>
              <div>{dayjs(createdAt).fromNow()}</div>
            </Tooltip>
          );
        },
        accessor: "createdAt",
        sortType: (rowA, rowB) =>
          dayjs(rowA.original.createdAt).unix() -
          dayjs(rowB.original.createdAt).unix(),
      },
      {
        Header: "Updated",
        Cell: ({
          value: updatedAt,
        }: CellProps<ConnectionData, ConnectionData["updatedAt"]>) => {
          return (
            <Tooltip content={<FormatTime date={updatedAt} format="LLL" />}>
              <div className="truncate">
                <FormatRelativeTime date={updatedAt} />
              </div>
            </Tooltip>
          );
        },
        accessor: "updatedAt",
        sortType: (rowA, rowB) =>
          dayjs(rowA.original.updatedAt).unix() -
          dayjs(rowB.original.updatedAt).unix(),
      },
      {
        accessor: "usageSummary",
        Header: "Sync Count",
        Cell: ({
          value: usageSummary,
        }: CellProps<ConnectionData, ConnectionData["usageSummary"]>) => {
          return (
            <Tooltip
              content={
                usageSummary.totalSyncs === 0 ? (
                  "This connector is not in use"
                ) : (
                  <List className="p-0">
                    {usageSummary.numEltSyncs > 0 && (
                      <List.Item className="p-0">
                        {usageSummary.numEltSyncs} Data Source{" "}
                        {usageSummary.numEltSyncs === 1 ? "sync" : "syncs"}
                      </List.Item>
                    )}
                    {usageSummary.numRevEltSyncs > 0 && (
                      <List.Item className="p-0">
                        {usageSummary.numRevEltSyncs} Reverse ETL{" "}
                        {usageSummary.numRevEltSyncs === 1 ? "sync" : "syncs"}
                      </List.Item>
                    )}
                  </List>
                )
              }
            >
              <div>
                {usageSummary.totalSyncs === 0 ? (
                  <TextMuted>-</TextMuted>
                ) : (
                  usageSummary.totalSyncs
                )}
              </div>
            </Tooltip>
          );
        },
        sortType: (rowA, rowB) =>
          rowA.original.usageSummary.totalSyncs -
          rowB.original.usageSummary.totalSyncs,
      },
      {
        id: "status",
        Header: "Status",
        Cell: ({ row }: CellProps<ConnectionData, ConnectionData["id"]>) => {
          return <Status connection={row.original} />;
        },
        width: 100,
        maxWidth: 100,
        disableSortBy: true,
      },
      ...(canDeleteConnection || canUpdateConnection
        ? [
            createStickyMenuColumn({
              accessor: "id",
              Cell: ({
                row,
                value: id,
              }: CellProps<ConnectionData, ConnectionData["id"]>) => (
                <ConnectionMenu
                  abilities={
                    integrations.get(row.original.integrationId)?.abilities ||
                    []
                  }
                  connectionId={id}
                  canDelete={canDeleteConnection}
                  canUpdate={canUpdateConnection}
                />
              ),
            }),
          ]
        : []),
    ],
    [integrations, canDeleteConnection, canUpdateConnection, restrictedGroups],
  );
}

const ConnectionMenu = ({
  connectionId,
  abilities,
  canDelete,
  canUpdate,
}: {
  connectionId: string;
  abilities: IntegrationAbility[];
  canDelete: boolean;
  canUpdate: boolean;
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const handleDelete = useDeleteItem({
    title: "connection",
    variables: {
      connectionId,
    },
    mutation: useRemoveConnectionMutation,
    refetchQueries: [
      {
        query: ConnectionsDocument,
      },
      {
        query: ConnectionsWithUsageSummaryDocument,
      },
    ],
  });
  const canRefreshDestination =
    canUpdate && abilities.includes(IntegrationAbility.ActivateDestination);

  if (!canDelete && !canRefreshDestination) return null;

  return (
    <>
      {canDelete && (
        <ConfirmDeleteModal
          title="connection"
          onConfirm={() => handleDelete()}
          show={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
        />
      )}
      <TableMenu>
        {canRefreshDestination && (
          <div className="py-1">
            <RefreshDestinationMenuItem connectionId={connectionId} />
          </div>
        )}
        {canDelete && (
          <div className="py-1">
            <MenuItem
              text="Delete"
              onClick={() => setShowConfirmModal(true)}
              danger={true}
              icon={<TrashIcon className="size-4" />}
            />
          </div>
        )}
      </TableMenu>
    </>
  );
};

const RefreshDestinationMenuItem = ({
  connectionId,
}: {
  connectionId: string;
}) => {
  const toast = useToast();

  const [refreshDestination] = useRefreshDestinationMutation({
    onError(error) {
      toast("Connection not refreshed", error.message, "error");
    },
    update: (cache) => {
      cache.modify({
        id: cache.identify({
          __typename: "Query",
        }),
        fields: {
          destinationProperties(_, { DELETE }) {
            return DELETE;
          },
          findAllAvailableOperations(_, { DELETE }) {
            return DELETE;
          },
        },
      });
    },

    onCompleted() {
      toast(
        "Connection refreshed",
        "Your connection has successfully been refreshed.",
        "success",
      );
    },
    refetchQueries: [
      { query: ConnectionsDocument, variables: { type: "destination" } },
    ],
  });

  const onRefreshDestination = () => {
    refreshDestination({
      variables: { connectionId: connectionId },
    });
  };

  return (
    <MenuItem
      text="Refresh object types"
      icon={<RefreshCwIcon className="size-4" />}
      onClick={onRefreshDestination}
    />
  );
};

const Status = (props: { connection: ConnectionData }) => {
  const { notifications } = useFilteredNotifications({
    connectionIds: [props.connection.id],
  });

  if (notifications.count) {
    return (
      <div className="flex">
        <CircleAlertIcon className="mr-1 size-5 shrink-0 text-red-600 dark:text-white" />
        <span>Suspended</span>
      </div>
    );
  }

  return (
    <div className="flex">
      <CircleCheckIcon className="mr-1 size-5 shrink-0 text-green-600 dark:text-white" />
      <span>Healthy</span>
    </div>
  );
};
